import { FRAMES, HUD, SPACING, UI_LOGICAL_SIZE } from "odyssey-shared";
import type { WindowBox } from "../../ui/kit/index.ts";
import { KIT_METRICS } from "../../ui/kit/metrics.ts";
import { previewZoom, type PreviewStage } from "./plan.ts";

/**
 * How the Look panel lays out, in logical pixels (the design card `look-step`, a visual reference, and ADR-094
 * decision 28): the window, the preview column at its left and the rows at its right. On a page too small for the
 * card's window the window takes what the page leaves inside the HUD margin, the preview column narrows and its
 * figure is drawn at a smaller whole zoom, and the rows scroll beside it.
 */

/**
 * The card's measures: the window (600 by 328 at 20, 16 on the 640 by 360 grid), the preview column (200), the gap
 * between the column and the rows (10), between rows (6), between a row's label and its control (3) and between the
 * rows and the notes (8); a swatch and the gap between swatches (16 and 2, and 24 and 4 for touch); a turn button's
 * side (the design's 12 glyph in the kit's glyph button, and 32 for touch); the facing word's width (36); and the
 * least width of the preview column.
 */
export const LOOK_PANEL = {
  width: 600,
  height: 328,
  top: 16,
  preview: 200,
  previewLeast: 64,
  gap: 10,
  rowGap: SPACING[4],
  headGap: 3,
  noteGap: SPACING[5],
  scrollGap: SPACING[3],
  swatch: 16,
  swatchGap: 2,
  touchSwatch: 24,
  touchSwatchGap: 4,
  turn: 16,
  touchTurn: 32,
  turnGap: 3,
  facing: 36,
} as const;

/**
 * The body the panel is laid out for under `react-dom/server`, and, unseen, until it is measured: the card's window
 * less its frame, its title bar, its footer and its padding.
 */
export const LOOK_BODY_DEFAULT = { width: 576, height: 256 } as const;

/**
 * The window's box in an area: the card's size or what the area leaves inside the HUD margin, centred across and
 * down by the card's share of the free height (16 of the 32 free on the 640 by 360 grid).
 *
 * @param area The scrim's size in logical pixels, as measured; a fraction of a pixel is dropped.
 * @returns The box in whole logical pixels.
 * @example
 * lookWindowBox({ width: 640, height: 360 });
 * // { left: 20, top: 16, width: 600, height: 328 }
 * lookWindowBox({ width: 445, height: 230 });
 * // { left: 4, top: 4, width: 437, height: 222 }
 */
export function lookWindowBox(area: { readonly width: number; readonly height: number }): WindowBox {
  const across = Math.floor(area.width);
  const down = Math.floor(area.height);
  const width = Math.max(0, Math.min(LOOK_PANEL.width, across - 2 * HUD.margin));
  const height = Math.max(0, Math.min(LOOK_PANEL.height, down - 2 * HUD.margin));
  const free = UI_LOGICAL_SIZE.height - LOOK_PANEL.height;
  return { left: Math.max(HUD.margin, Math.floor((across - width) / 2)), top: Math.max(HUD.margin, Math.floor(((down - height) * LOOK_PANEL.top) / free)), width, height };
}

/**
 * The preview column in a body: its width and height, the room its figure has, and the zoom the figure is drawn at.
 */
export interface PreviewColumn {
  readonly width: number;
  readonly height: number;
  readonly room: { readonly width: number; readonly height: number };
  readonly zoom: number;
}

/**
 * Lays out the preview column: the card's 200 wide where the body has room, and otherwise five fourteenths of the
 * body less the gap (the card's share), never narrower than the least; as tall as the body. The figure's room is
 * the column inside its panel's frame and padding, less the turn row and the gap above it, and its zoom the largest
 * whole one that fits.
 *
 * @param body The window body's content size in logical pixels, as measured.
 * @param stage The stage the figure is drawn in, or null when there is no figure.
 * @param touch Whether the turn row is the touch size.
 * @returns The column, in whole logical pixels.
 * @example
 * previewColumn({ width: 576, height: 256 }, { left: -9, top: -32, width: 19, height: 34 }, false);
 * // { width: 200, height: 256, room: { width: 188, height: 225 }, zoom: 6 }
 */
export function previewColumn(body: { readonly width: number; readonly height: number }, stage: PreviewStage | null, touch: boolean): PreviewColumn {
  const width = Math.max(LOOK_PANEL.previewLeast, Math.min(LOOK_PANEL.preview, Math.floor(((Math.floor(body.width) - LOOK_PANEL.gap) * 5) / 14)));
  const height = Math.max(0, Math.floor(body.height));
  const chromeX = FRAMES.panel.insets.left + FRAMES.panel.insets.right + 2 * KIT_METRICS.panel.padding;
  const chromeY = FRAMES.panel.insets.top + FRAMES.panel.insets.bottom + 2 * KIT_METRICS.panel.padding;
  const turn = touch ? LOOK_PANEL.touchTurn : LOOK_PANEL.turn;
  const room = { width: Math.max(0, width - chromeX), height: Math.max(0, height - chromeY - turn - LOOK_PANEL.turnGap) };
  return { width, height, room, zoom: stage === null ? 1 : previewZoom(room, stage) };
}
